import React, { useEffect, useRef, useState, useContext } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import Lenis from "lenis";
import { InkText, MaskLine, Fade } from "./InkText";
import { BrandLogo } from "./BrandLogos";
import { CaseStudyContext, CaseStudyOverlay, PROJECTS } from "./CaseStudy";

/* clickable logo -> opens case study */
const CaseTrigger = ({ id, align = "left", children }) => {
  const open = useContext(CaseStudyContext);
  const a = align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  return (
    <button onClick={() => open(id)} data-testid={`open-case-${id}`} className="group block w-full cursor-pointer">
      {children}
      <div className={`mt-3 sm:mt-5 font-mono2 text-[10px] sm:text-xs uppercase tracking-[0.25em] opacity-60 group-hover:opacity-100 transition-opacity ${a}`}>
        <span className="inline-block group-hover:translate-x-1 transition-transform">View case study →</span>
      </div>
    </button>
  );
};

/* ---------------- helpers ---------------- */
function useIsMobile(bp = 768) {
  const [m, setM] = useState(typeof window !== "undefined" ? window.innerWidth < bp : false);
  useEffect(() => {
    const on = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [bp]);
  return m;
}

const Marquee = ({ items, className = "" }) => (
  <div className={`overflow-hidden w-full ${className}`} data-testid="marquee">
    <div className="marquee-track">
      {[0, 1].map((k) => (
        <span key={k} className="shrink-0">
          {items.map((it, i) => (
            <span key={i} className="mx-6 inline-block align-middle">
              {it}
              <span className="mx-6 align-middle" style={{ color: "var(--red)" }}>&bull;</span>
            </span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

/* ---------------- HERO ---------------- */
const HeroPanel = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-center px-6 sm:px-14 lg:px-24" data-testid="panel-hero">
    <div className="absolute top-8 left-6 sm:left-14 lg:left-24 font-mono2 text-xs uppercase tracking-[0.28em]">
      Portfolio &middot; 2025 / 26
    </div>
    <div className="absolute top-8 right-6 sm:right-14 lg:right-24 font-mono2 text-xs uppercase tracking-[0.28em] text-right hidden sm:block">
      Montréal &middot; Remote
    </div>

    <div className="max-w-[1100px]">
      <MaskLine delay={0.15} className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.35em] mb-5 sm:mb-8">
        Social Media Manager &middot; Brand Strategist &middot; Visual Brand Builder
      </MaskLine>

      <h1 className="font-corm font-light leading-[0.82] tracking-tight text-[19vw] sm:text-[15vw] lg:text-[12.5vw]">
        <MaskLine delay={0.28}>Alejandra</MaskLine>
        <MaskLine delay={0.42}>
          <span>Haddad</span>
          <span style={{ color: "var(--red)" }}>.</span>
        </MaskLine>
      </h1>
    </div>

    <motion.div
      className="absolute bottom-8 left-6 sm:left-14 lg:left-24 flex items-center gap-3 font-mono2 text-xs uppercase tracking-[0.28em]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.4, duration: 1 }}
    >
      <motion.span
        animate={{ x: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
      >
        &rarr;
      </motion.span>
      <span className="hidden sm:inline">unroll the poster</span>
      <span className="sm:hidden">scroll down</span>
    </motion.div>
  </div>
);

/* ---------------- MANIFESTO ---------------- */
const CHAPTERS = [
  ["01", "I sell with words,", "not noise. Every brand gets one sharp idea and the discipline to repeat it until it sticks."],
  ["02", "A logo is a sentence", "compressed into a single mark. I design the mark and the whole grammar around it."],
  ["03", "Feeds are pages", "in a longer story. I plan the rhythm so the scroll reads like an edited magazine."],
];
const ManifestoPanel = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-center px-6 sm:px-14 lg:px-24 py-20" data-testid="panel-manifesto">
    <Fade className="font-mono2 text-xs uppercase tracking-[0.3em] mb-10 lg:mb-16" style={{ color: "var(--red)" }}>
      <span style={{ color: "var(--red)" }}>&sect; How I work</span>
    </Fade>
    <div className="flex flex-col gap-10 lg:gap-14 max-w-5xl">
      {CHAPTERS.map(([n, a, b], i) => (
        <div key={n} className="flex flex-col sm:flex-row gap-4 sm:gap-10 border-t border-neutral-300 pt-6">
          <span className="font-mono2 text-sm pt-2 sm:pt-3 shrink-0" style={{ color: "var(--red)" }}>{n}</span>
          <p className="font-corm font-light text-3xl sm:text-4xl lg:text-6xl leading-[1.02] tracking-tight">
            <InkText text={a} delay={i * 0.05} />{" "}
            <span className="text-neutral-500"><InkText text={b} delay={i * 0.05 + 0.1} /></span>
          </p>
        </div>
      ))}
    </div>
  </div>
);

/* ---------------- OFFER / SERVICES ---------------- */
const OFFERS = [
  "Content Creation (Reels, Stories, Posts)",
  "Photography & Video Editing",
  "Personalized strategy tailored to your business",
  "Adaptation to trends",
  "Optimization to attract and convert customers",
];
const OfferPanel = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-center px-6 sm:px-14 lg:px-24 py-20" data-testid="panel-offer">
    <Fade className="font-mono2 text-xs uppercase tracking-[0.3em] mb-6" style={{ color: "var(--red)" }}>
      <span style={{ color: "var(--red)" }}>&sect; Services</span>
    </Fade>
    <InkText as="h2" text="What do I offer?" className="font-corm font-light leading-[0.9] tracking-tight text-5xl sm:text-6xl lg:text-7xl block mb-10 lg:mb-14" />
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
      <div className="lg:col-span-7">
        {OFFERS.map((o, i) => (
          <Fade key={o} delay={i * 0.08} className="flex items-baseline gap-5 border-t border-neutral-300 py-4 sm:py-5">
            <span className="font-mono2 text-sm shrink-0" style={{ color: "var(--red)" }}>{String(i + 1).padStart(2, "0")}</span>
            <p className="font-corm font-light text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">{o}</p>
          </Fade>
        ))}
      </div>
      <div className="lg:col-span-5">
        <Fade delay={0.3}>
          <p className="font-corm italic text-2xl sm:text-3xl lg:text-4xl leading-[1.15]">
            My focus is clear: helping your business <span style={{ color: "var(--red)" }}>grow</span>, establish its position, and <span style={{ color: "var(--red)" }}>increase sales</span>.
          </p>
        </Fade>
      </div>
    </div>
  </div>
);

/* ---------------- SKILLS / CAPABILITIES ---------------- */
const COMPETENCIES = [
  ["Marketing & Analytics", ["Vendor & supplier coordination", "Service delivery & compliance", "KPI tracking & reporting", "Multi-project & priority management", "Campaign development & execution", "Lead generation"]],
  ["Creative & Content", ["Short-form video production & editing", "Premiere Pro · Final Cut Pro · CapCut", "Adobe CC · Photoshop, Illustrator, Acrobat", "Copywriting"]],
  ["Operations & Tools", ["Microsoft Office Suite", "Excel · Word · PowerPoint · Outlook · Visio", "Cross-cultural coordination (CA / CO)", "Team leadership", "Discretion & attention to detail"]],
  ["Languages", ["French", "English", "Spanish", "Italian"]],
];
const SkillsPanel = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-center px-6 sm:px-14 lg:px-24 py-20" data-testid="panel-skills">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
      <div className="lg:col-span-5">
        <Fade className="font-mono2 text-xs uppercase tracking-[0.3em] mb-6" style={{ color: "var(--red)" }}>
          <span style={{ color: "var(--red)" }}>&sect; Capabilities</span>
        </Fade>
        <InkText as="h2" text="What I bring to the table." className="font-corm font-light leading-[0.92] tracking-tight text-4xl sm:text-5xl lg:text-6xl block" />
        <Fade delay={0.2} className="mt-8 max-w-md">
          <p className="font-outfit text-sm lg:text-base leading-relaxed text-neutral-700">
            Operations &amp; service-delivery professional with 5+ years across retail
            management, vendor coordination and client-facing marketing, comfortable
            juggling many priorities, partners and compliance standards at once.
          </p>
        </Fade>
      </div>
      <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
        {COMPETENCIES.map(([title, items], i) => (
          <Fade key={title} delay={i * 0.08} className="border-t border-neutral-300 pt-4">
            <h3 className="font-mono2 text-xs uppercase tracking-[0.22em] mb-3 flex items-center gap-2">
              <span style={{ color: "var(--red)" }}>{String(i + 1).padStart(2, "0")}</span>
              {title}
            </h3>
            <ul className="font-outfit text-sm leading-relaxed text-neutral-700 space-y-1">
              {items.map((it) => (<li key={it}>{it}</li>))}
            </ul>
          </Fade>
        ))}
      </div>
    </div>
  </div>
);

/* ---------------- PROJECT PANELS ---------------- */
const Tag = ({ children, color }) => (
  <span className="font-mono2 text-[10px] sm:text-xs uppercase tracking-[0.25em] border px-3 py-1 rounded-full" style={{ borderColor: color }}>
    {children}
  </span>
);

const AutoRoble = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-between px-6 sm:px-14 lg:px-24 py-16" style={{ background: "#9B1B1B", color: "#F4F1EB" }} data-testid="panel-autoroble">
    <div className="flex justify-between items-start">
      <span className="font-mono2 text-xs tracking-[0.3em]">01 / 06</span>
      <span className="font-mono2 text-xs tracking-[0.3em]">@autoroble</span>
    </div>
    <CaseTrigger id="autoroble">
      <Fade className="w-full flex justify-start" data-testid="logo-autoroble">
        <BrandLogo brand="autoroble" className="w-full max-w-[340px]" />
      </Fade>
    </CaseTrigger>
    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
      <p className="font-anton uppercase tracking-wide text-xl sm:text-2xl lg:text-3xl">Creciendo con calidad</p>
      <p className="font-outfit max-w-sm text-sm leading-relaxed opacity-90">
        Automotive service &amp; parts. Built a bold, high-contrast identity and a
        weekly content engine that turned quiet listings into a loud, trusted garage.
      </p>
      <div className="flex flex-wrap gap-2">
        <Tag color="#F4F1EB">Identity</Tag><Tag color="#F4F1EB">Social</Tag><Tag color="#F4F1EB">Campaigns</Tag>
      </div>
    </div>
  </div>
);

const TacosFrida = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-center px-6 sm:px-14 lg:px-24 py-16" style={{ background: "#FF2E88", color: "#FFFFFF" }} data-testid="panel-tacosfridamtl">
    <div className="absolute top-16 left-6 sm:left-14 lg:left-24 font-mono2 text-xs tracking-[0.3em]">02 / 06</div>
    <div className="absolute top-16 right-6 sm:right-14 lg:right-24 font-mono2 text-xs tracking-[0.3em]">@tacosfridamtl</div>
    <CaseTrigger id="tacosfridamtl" align="center">
      <Fade className="-rotate-2 w-full flex justify-center" data-testid="logo-tacosfrida">
        <BrandLogo brand="tacosfridamtl" className="w-full max-w-[340px]" />
      </Fade>
    </CaseTrigger>
    <div className="mt-10 flex flex-col sm:flex-row gap-6 justify-between items-start">
      <p className="font-caveat text-3xl sm:text-4xl">Antojitos con alma.</p>
      <p className="font-outfit max-w-sm text-sm leading-relaxed">
        A Mexican kitchen in Montréal. Hand-lettered menus, saturated colour and playful
        drops gave the feed the warmth of a family table.
      </p>
      <div className="flex flex-wrap gap-2">
        <Tag color="#FFFFFF">Lettering</Tag><Tag color="#FFFFFF">Menu Drops</Tag><Tag color="#FFFFFF">Content</Tag>
      </div>
    </div>
  </div>
);

const DistriRoble = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-center px-6 sm:px-14 lg:px-24 py-16" style={{ background: "#17294F", color: "#F4F1EB" }} data-testid="panel-distriroble">
    <div className="flex justify-between font-mono2 text-xs tracking-[0.3em] mb-10">
      <span>03 / 06</span><span>@distriroble</span>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
      <div className="lg:col-span-8">
        <CaseTrigger id="distriroble"><Fade data-testid="logo-distriroble" className="flex justify-start"><BrandLogo brand="distriroble" className="w-full max-w-[300px]" /></Fade></CaseTrigger>
      </div>
      <div className="lg:col-span-4 font-mono2 text-sm leading-relaxed space-y-4">
        <p className="border-l-2 pl-4" style={{ borderColor: "#9B1B1B" }}>Más que una entrega.</p>
        <p className="opacity-80">Official Hyundai dealer in Montería. A crisp, confident feed: model
          launches, test-drive invites and delivery-day moments. #MiHyundai.</p>
        <div className="pt-2 space-y-1 opacity-70">
          <div className="flex justify-between border-t border-[#F4F1EB]/25 pt-1"><span>SHOWROOM</span><span>SOCIAL</span></div>
          <div className="flex justify-between border-t border-[#F4F1EB]/25 pt-1"><span>LAUNCHES</span><span>REELS</span></div>
          <div className="flex justify-between border-t border-[#F4F1EB]/25 pt-1"><span>BRAND</span><span>HYUNDAI</span></div>
        </div>
      </div>
    </div>
  </div>
);

const VinilTV = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-center px-6 sm:px-14 lg:px-24 py-16" style={{ background: "#0E1116", color: "#1CA0E3" }} data-testid="panel-viniltv">
    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 opacity-[0.08] pointer-events-none">
      <Marquee items={["VINIL", "TV", "ON AIR", "REC", "PLAY"]} className="font-syne font-extrabold text-[16vw] uppercase" />
    </div>
    <div className="flex justify-between font-mono2 text-xs tracking-[0.3em] mb-8 relative">
      <span>04 / 06</span><span className="flex items-center gap-2"><span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: "#F5821F" }} />@viniltv</span>
    </div>
    <CaseTrigger id="viniltv">
      <Fade className="relative w-full flex justify-start" data-testid="logo-viniltv">
        <BrandLogo brand="viniltv" className="w-full max-w-[360px]" />
      </Fade>
    </CaseTrigger>
    <div className="relative mt-8 flex flex-col sm:flex-row justify-between gap-6 items-start">
      <p className="font-syne font-bold text-2xl sm:text-3xl" style={{ color: "#F5821F" }}>Press play on culture.</p>
      <p className="font-outfit max-w-sm text-sm leading-relaxed text-neutral-300">
        Music, media &amp; broadcast. A neon, retro-future voice, bold Syne type and
        motion cues built to feel like a channel that never stops transmitting.
      </p>
      <div className="flex flex-wrap gap-2">
        <Tag color="#1CA0E3">Brand</Tag><Tag color="#1CA0E3">Motion</Tag><Tag color="#1CA0E3">Series</Tag>
      </div>
    </div>
  </div>
);

const Ora = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-center items-center text-center px-6 sm:px-14 lg:px-24 py-16" style={{ background: "#F4F1EB", color: "#141414" }} data-testid="panel-ora">
    <div className="absolute top-16 left-6 sm:left-14 lg:left-24 font-mono2 text-xs tracking-[0.3em]">05 / 06</div>
    <div className="absolute top-16 right-6 sm:right-14 lg:right-24 font-mono2 text-xs tracking-[0.3em] italic font-playfair">ORA Boutique</div>
    <CaseTrigger id="ora" align="center"><Fade className="w-full flex justify-center" data-testid="logo-ora"><BrandLogo brand="ora" className="w-full max-w-[360px]" /></Fade></CaseTrigger>
    <Fade delay={0.3} className="max-w-lg">
      <p className="font-playfair italic text-xl sm:text-2xl leading-relaxed">
        &ldquo;Wear the season.&rdquo; An elegant fashion house identity, a refined serif
        wordmark, a botanical accent and a gold-lit feed with timeless restraint.
      </p>
    </Fade>
    <div className="flex flex-wrap gap-2 justify-center mt-8">
      <Tag color="#141414">Logo</Tag><Tag color="#141414">Editorial</Tag><Tag color="#141414">Feed Art</Tag>
    </div>
  </div>
);

const Emporium = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-center px-6 sm:px-14 lg:px-24 py-16" style={{ background: "#F4F1EB", color: "#9B1B1B" }} data-testid="panel-emporium">
    <div className="flex justify-between font-mono2 text-xs tracking-[0.3em] mb-10">
      <span>06 / 06</span><span>Logo &middot; Editorial</span>
    </div>
    <div className="text-right">
      <CaseTrigger id="emporium" align="right"><Fade className="flex justify-end" data-testid="logo-emporium"><BrandLogo brand="emporium" className="w-full max-w-[330px]" /></Fade></CaseTrigger>
      <div className="h-px w-full my-6" style={{ background: "#9B1B1B", opacity: 0.3 }} />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 text-left">
        <p className="font-bodoni italic text-2xl sm:text-3xl">The art of the acquired.</p>
        <p className="font-outfit max-w-sm text-sm leading-relaxed text-neutral-700">
          A curated Montr&eacute;al emporium. High-fashion Bodoni contrast, an archway
          heritage mark and a near-monochrome editorial system dressed in deep red.
        </p>
        <div className="flex flex-wrap gap-2">
          <Tag color="#9B1B1B">Logo</Tag><Tag color="#9B1B1B">Art Direction</Tag>
        </div>
      </div>
    </div>
  </div>
);

/* ---------------- SELECTED SHOOTS (photo/video reel) ---------------- */
const IG = {
  auto: "https://www.instagram.com/autoroble/",
  tacos: "https://www.instagram.com/tacosfridamtl/",
  distri: "https://www.instagram.com/distriroble/",
  vinil: "https://www.instagram.com/viniltv/",
};
const SHOOTS = [
  ["01", "Tacos Frida", "Al Pastor Trompo", "REEL", "9:16 · 0:15", true, IG.tacos, "'25"],
  ["02", "Auto Roble", "Antes / Después", "VIDEO", "16:9 · 0:45", true, IG.auto, "'25"],
  ["03", "Distri Roble", "La Nueva Kona", "VIDEO", "16:9 · 1:00", true, IG.distri, "'25"],
  ["04", "Tacos Frida", "Birria & Consomé", "REEL", "9:16 · 0:20", true, IG.tacos, "'25"],
  ["05", "Vinil TV", "On Air · Ep.07", "REEL", "9:16 · 0:30", true, IG.vinil, "'25"],
  ["06", "Auto Roble", "Taller en Vivo", "PHOTO", "3:2", false, IG.auto, "'25"],
  ["07", "Tacos Frida", "Cochinita Sundays", "REEL", "9:16 · 0:18", true, IG.tacos, "'26"],
  ["08", "Distri Roble", "Palisade 2026", "VIDEO", "16:9 · 0:50", true, IG.distri, "'26"],
  ["09", "Vinil TV", "Nueva Temporada", "REEL", "9:16 · 0:35", true, IG.vinil, "'26"],
  ["10", "Auto Roble", "Diagnóstico Live", "REEL", "9:16 · 0:22", true, IG.auto, "'26"],
  ["11", "Distri Roble", "Día de Entrega", "REEL", "9:16 · 0:25", true, IG.distri, "'26"],
  ["12", "Vinil TV", "Detrás del Show", "PHOTO", "4:5", false, IG.vinil, "'26"],
];

const FilmFrame = ({ no, brand, title, format, spec, isVideo, href, year }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`shoot-${no}`}
    className="shrink-0 w-[260px] sm:w-[320px] mx-2 h-full relative flex flex-col justify-between p-5 group cursor-pointer transition-[filter] duration-300 hover:brightness-[0.97]"
    style={{ background: "#F4F1EB", color: "#141414" }}
  >
    <div className="flex justify-between items-center font-mono2 text-[10px] uppercase tracking-[0.2em]">
      <span className="opacity-60">{no} · {year}</span>
      <span className="flex items-center gap-1.5" style={{ color: "var(--red)" }}>
        {isVideo ? <><span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--red)" }} />REC</> : "PHOTO"}
      </span>
    </div>
    <div className="flex-1 flex items-center justify-center">
      {isVideo ? (
        <span className="flex items-center justify-center w-16 h-16 rounded-full border-2 group-hover:scale-110 transition-transform" style={{ borderColor: "#141414" }}>
          <span style={{ width: 0, height: 0, borderTop: "10px solid transparent", borderBottom: "10px solid transparent", borderLeft: "16px solid var(--red)", marginLeft: "4px" }} />
        </span>
      ) : (
        <span className="w-14 h-14 rounded-full border-2 flex items-center justify-center group-hover:scale-110 transition-transform" style={{ borderColor: "#141414" }}>
          <span className="w-6 h-6 rounded-full border-2" style={{ borderColor: "var(--red)" }} />
        </span>
      )}
    </div>
    <div>
      <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] flex items-center justify-between">
        <span className="opacity-50">{brand}</span>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--red)" }}>Instagram ↗</span>
      </div>
      <h3 className="font-corm font-light text-2xl sm:text-3xl leading-none mt-1">{title}</h3>
      <div className="mt-3 flex justify-between font-mono2 text-[10px] uppercase tracking-[0.2em] border-t border-neutral-300 pt-2">
        <span>{format}</span><span className="opacity-60">{spec}</span>
      </div>
    </div>
  </a>
);

const sprocketStyle = { backgroundImage: "repeating-linear-gradient(to right, #F4F1EB 0 11px, transparent 11px 30px)" };

const ShootsPanel = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-center px-6 sm:px-14 lg:px-24 py-16" data-testid="panel-shoots">
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 lg:mb-12">
      <div>
        <Fade className="font-mono2 text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "var(--red)" }}>
          <span style={{ color: "var(--red)" }}>&sect; Showreel</span>
        </Fade>
        <InkText as="h2" text="Selected Shoots" className="font-corm font-light leading-[0.9] tracking-tight text-5xl sm:text-6xl lg:text-7xl block" />
      </div>
      <Fade delay={0.2} className="font-mono2 text-xs uppercase tracking-[0.25em] text-neutral-600">
        Photography &amp; Video &middot; 2025/26
      </Fade>
    </div>

    {/* filmstrip */}
    <div className="relative w-full overflow-hidden" style={{ background: "#141414", height: "clamp(260px, 46vh, 420px)" }} data-testid="filmstrip">
      <div className="absolute top-0 inset-x-0 h-4 opacity-80" style={sprocketStyle} />
      <div className="absolute bottom-0 inset-x-0 h-4 opacity-80" style={sprocketStyle} />
      <div className="absolute inset-x-0 top-4 bottom-4 flex items-stretch">
        <div className="marquee-track film-track flex h-full items-stretch">
          {[0, 1].map((k) => (
            <span key={k} className="flex h-full items-stretch">
              {SHOOTS.map((s, i) => (
                <FilmFrame key={`${k}-${i}`} no={s[0]} brand={s[1]} title={s[2]} format={s[3]} spec={s[4]} isVideo={s[5]} href={s[6]} />
              ))}
            </span>
          ))}
        </div>
      </div>
    </div>
    <Fade delay={0.2} className="mt-5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
      Hover to pause &middot; a typographic cut of recent photo &amp; video work
    </Fade>
  </div>
);

/* ---------------- CONTACT ---------------- */
const ContactPanel = () => (
  <div className="relative h-full min-h-screen w-full overflow-hidden flex flex-col justify-center px-6 sm:px-14 lg:px-24 py-20" data-testid="panel-contact">
    <Fade className="font-mono2 text-xs uppercase tracking-[0.3em] mb-6" style={{ color: "var(--red)" }}>
      <span style={{ color: "var(--red)" }}>&sect; Let&rsquo;s make something worth printing</span>
    </Fade>
    <a href="mailto:alejandraojedahaddad@hotmail.com" data-testid="contact-email" className="group block">
      <InkText
        as="span"
        text="alejandraojedahaddad@hotmail.com"
        className="font-corm font-light leading-[0.95] tracking-tight text-[8vw] sm:text-[6.5vw] lg:text-[5.2vw] break-all group-hover:opacity-60 transition-opacity duration-500 block"
      />
      <span className="block h-[3px] mt-2 w-0 group-hover:w-full transition-all duration-700" style={{ background: "var(--red)" }} />
    </a>
    <div className="mt-14 flex flex-col sm:flex-row gap-8 sm:gap-16 font-mono2 text-sm uppercase tracking-[0.2em]">
      <a href="https://www.linkedin.com/in/alejandrahaddad/" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin" className="hover:text-[color:var(--red)] transition-colors">
        LinkedIn ↗
      </a>
      <span className="text-neutral-500">&copy; 2026 A. Haddad</span>
    </div>
    <div className="mt-16 border-t border-neutral-300 pt-6">
      <Marquee items={["Branding", "Social Media", "Logo Design", "Content", "Campaigns", "Art Direction"]} className="font-corm text-4xl sm:text-6xl italic" />
    </div>
  </div>
);

/* ---------------- assembly ---------------- */
const PANELS = [
  ["hero", <HeroPanel />],
  ["manifesto", <ManifestoPanel />],
  ["offer", <OfferPanel />],
  ["skills", <SkillsPanel />],
  ["autoroble", <AutoRoble />],
  ["tacos", <TacosFrida />],
  ["distri", <DistriRoble />],
  ["vinil", <VinilTV />],
  ["ora", <Ora />],
  ["emporium", <Emporium />],
  ["shoots", <ShootsPanel />],
  ["contact", <ContactPanel />],
];

const ProgressBar = ({ progress }) => {
  const scaleX = useSpring(progress, { stiffness: 120, damping: 30, mass: 0.4 });
  return (
    <motion.div
      className="fixed bottom-0 left-0 right-0 h-[3px] origin-left z-[10000]"
      style={{ scaleX, background: "var(--red)" }}
      data-testid="scroll-progress"
    />
  );
};

const HorizontalStage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], ["0vw", `-${(PANELS.length - 1) * 100}vw`]);
  return (
    <section ref={ref} style={{ height: `${PANELS.length * 100}vh` }} className="relative" data-testid="horizontal-stage">
      <div className="sticky top-0 h-screen overflow-hidden">
        <motion.div style={{ x }} className="flex h-screen">
          {PANELS.map(([key, node]) => (
            <div key={key} className="w-screen h-screen shrink-0 relative overflow-hidden">
              {node}
              <span className="fold-line" style={{ left: 0 }} />
            </div>
          ))}
        </motion.div>
      </div>
      <ProgressBar progress={scrollYProgress} />
    </section>
  );
};

const VerticalStack = () => (
  <div data-testid="vertical-stack">
    {PANELS.map(([key, node]) => (
      <div key={key} className="w-full relative">{node}</div>
    ))}
  </div>
);

export default function Portfolio() {
  const isMobile = useIsMobile();
  const [activeId, setActiveId] = useState(null);
  const lenisRef = useRef(null);
  const activeProject = PROJECTS.find((p) => p.id === activeId) || null;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (activeId) lenisRef.current?.stop();
    else lenisRef.current?.start();
  }, [activeId]);

  return (
    <CaseStudyContext.Provider value={setActiveId}>
      <main className="relative bg-[color:var(--paper)] text-[color:var(--ink)]">
        <div className="paper-grain" />
        <div className="fixed top-5 left-6 sm:left-14 lg:left-24 z-[9998] mix-blend-difference text-white font-corm italic text-lg tracking-tight" data-testid="brand-mark">
          A · H
        </div>
        {isMobile ? <VerticalStack /> : <HorizontalStage />}
      </main>
      <CaseStudyOverlay project={activeProject} onClose={() => setActiveId(null)} />
    </CaseStudyContext.Provider>
  );
}
